import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Layout, Avatar } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  /* Tab 一级图标（线条风，14px，0.7 透明度） */
  RobotOutlined, PictureOutlined, CompassOutlined, ApartmentOutlined,
  PlayCircleOutlined, RadarChartOutlined, AimOutlined, ThunderboltOutlined,
  LineChartOutlined, ExperimentOutlined, GlobalOutlined,
  /* 下拉网格子项图标 */
  BookOutlined, DatabaseOutlined, CalendarOutlined,
  BarChartOutlined, TeamOutlined, ClockCircleOutlined,
  HeartOutlined, AppstoreOutlined, ShoppingOutlined,
  CarOutlined, ShopOutlined, SafetyOutlined,
  LayoutOutlined, SettingOutlined,
  /* 右侧常驻 */
  MessageOutlined, PlusOutlined, DownOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../stores/auth';
import FireflyCanvas from '../components/magic/FireflyCanvas';
import { getPendingCount } from '../api/approval';

const { Header, Content } = Layout;

/* ============================================================
 *  单行高密度 Tab 栏样式（Chrome 标签页风格）
 *  - overflow-x:auto 但隐藏滚动条
 *  - 支持滚轮 / 触控板横向滑动
 *  - 右侧渐隐遮罩显式提示可滚动
 * ============================================================ */
const NAV_CSS = `
.sc-nav-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
  mask-image: linear-gradient(to right, #000 0, #000 calc(100% - 24px), transparent 100%);
  -webkit-mask-image: linear-gradient(to right, #000 0, #000 calc(100% - 24px), transparent 100%);
}
.sc-nav-scroll::-webkit-scrollbar { display: none; width: 0; height: 0; }

@keyframes scShimmer {
  0%   { transform: translateX(-130%); }
  100% { transform: translateX(240%); }
}
@keyframes scBreathe {
  0%, 100% { box-shadow: 0 3px 12px rgba(0, 122, 255, 0.26); }
  50%      { box-shadow: 0 5px 20px rgba(88, 86, 214, 0.44); }
}
`;

/* ============================================================
 *  导航配置 —— 单行 13 个一级标签
 *  11 个直连（核心技术词全部平铺） + 2 个下拉展开器
 *  26 条路由 100% 保留，0 删除
 * ============================================================ */
export const NAVIGATION = [
  { id: 'ai-os', label: 'AI OS', path: '/ai-os', isCapsule: true, icon: <RobotOutlined /> },
  { id: 'creation', label: '创作', path: '/projects', icon: <PictureOutlined /> },
  { id: 'community', label: '广场', path: '/community', icon: <CompassOutlined /> },
  { id: 'orchestration', label: '编排', path: '/agent-orchestration', icon: <ApartmentOutlined /> },
  { id: 'execution', label: '执行', path: '/execution', icon: <PlayCircleOutlined /> },
  { id: 'monitor', label: '监控', path: '/monitor', icon: <RadarChartOutlined /> },
  { id: 'opportunity', label: '机会', path: '/opportunity-radar', icon: <AimOutlined /> },
  { id: 'decision', label: '决策', path: '/decision', icon: <ThunderboltOutlined /> },
  { id: 'analytics', label: '数据', path: '/analytics', icon: <LineChartOutlined /> },
  { id: 'abtest', label: 'A/B测试', path: '/ab-test', icon: <ExperimentOutlined /> },
  { id: 'geo', label: 'GEO', path: '/geo', icon: <GlobalOutlined /> },
  {
    id: 'engine-more',
    label: '引擎',
    path: '/knowledge-base',
    icon: <DatabaseOutlined />,
    columns: 3,
    width: 430,
    children: [
      { label: '知识库', path: '/knowledge-base', icon: <BookOutlined /> },
      { label: '记忆', path: '/memory', icon: <DatabaseOutlined /> },
      { label: '日历', path: '/content-calendar', icon: <CalendarOutlined /> },
      { label: '效果', path: '/marketing', icon: <BarChartOutlined /> },
      { label: '达人', path: '/influencer', icon: <TeamOutlined /> },
      { label: '调度', path: '/launches', icon: <ClockCircleOutlined /> },
    ],
  },
  {
    id: 'more',
    label: '更多',
    path: '/life',
    icon: <AppstoreOutlined />,
    columns: 3,
    width: 470,
    children: [
      { label: '生活', path: '/life', icon: <HeartOutlined /> },
      { label: '技能', path: '/marketplace', icon: <AppstoreOutlined /> },
      { label: '购物', path: '/shopping', icon: <ShoppingOutlined /> },
      { label: '服务', path: '/services', icon: <CarOutlined /> },
      { label: '商家', path: '/business', icon: <ShopOutlined /> },
      { label: '团队', path: '/team', icon: <TeamOutlined /> },
      { label: '审批', path: '/approval-center', icon: <SafetyOutlined />, showBadge: true },
      { label: '设计系统', path: '/design-system', icon: <LayoutOutlined /> },
      { label: '设置', path: '/settings', icon: <SettingOutlined /> },
    ],
  },
];

/** 拍平后的全部可导航路由，用于 active 判定的最长前缀匹配 */
const allNavPaths = NAVIGATION.flatMap((item) =>
  item.children ? item.children.map((c) => c.path) : [item.path]
);

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const { i18n } = useTranslation();

  const [scrolled, setScrolled] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  /* 下拉面板状态：openId / mounted(退场) / entered(进场) / pos / pressing */
  const [openId, setOpenId] = useState(null);
  const [panelMounted, setPanelMounted] = useState(false);
  const [panelEntered, setPanelEntered] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const [pressing, setPressing] = useState(null);
  /* 滑动下划线：{ left, width } 相对 nav 内容层 */
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });

  const navScrollRef = useRef(null);
  const navInnerRef = useRef(null);
  const openIdRef = useRef(null);
  const triggerRefs = useRef({});
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    getPendingCount().then(setPendingCount).catch(() => {});
  }, []);

  useEffect(() => () => {
    clearTimeout(openTimerRef.current);
    clearTimeout(closeTimerRef.current);
  }, []);

  const switchLanguage = () => {
    i18n.changeLanguage(i18n.language === 'en-US' ? 'zh-CN' : 'en-US');
  };

  /* —— 滚轮 / 触控板横向滑动（Chrome 标签页风格）—— */
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (e.deltaY === 0) return;
      if (el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  /** 最长前缀匹配，确定当前激活路由 */
  const activePath = useMemo(() => {
    const path = location.pathname;
    let best = '';
    for (const p of allNavPaths) {
      if (path === p || path.startsWith(p + '/')) {
        if (p.length > best.length) best = p;
      }
    }
    return best;
  }, [location.pathname]);

  /** 当前激活的一级标签 id（含下拉展开器命中子项时） */
  const activeId = useMemo(() => {
    for (const item of NAVIGATION) {
      if (item.children) {
        if (item.children.some((c) => c.path === activePath)) return item.id;
      } else if (item.path === activePath) {
        return item.id;
      }
    }
    return null;
  }, [activePath]);

  /**
   * 测量并更新滑动下划线位置。
   * 依赖 navInner 内的 [data-tab-id] 元素与其 offsetLeft/offsetWidth。
   */
  const updateIndicator = useCallback(() => {
    const inner = navInnerRef.current;
    if (!inner) return;
    const el = inner.querySelector('[data-tab-active="true"]');
    if (!el) {
      setIndicator((p) => ({ ...p, ready: false }));
      return;
    }
    setIndicator({ left: el.offsetLeft, width: el.offsetWidth, ready: true });
  }, []);

  useEffect(() => {
    // 等待布局稳定后测量（字体加载 / 首次渲染）
    const raf = requestAnimationFrame(() => updateIndicator());
    return () => cancelAnimationFrame(raf);
  }, [activeId, updateIndicator]);

  useEffect(() => {
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [updateIndicator]);

  /** 激活项自动滚动进可视区 */
  useEffect(() => {
    const inner = navInnerRef.current;
    const scroll = navScrollRef.current;
    if (!inner || !scroll) return;
    const el = inner.querySelector('[data-tab-active="true"]');
    if (!el) return;
    const elLeft = el.offsetLeft;
    const elRight = elLeft + el.offsetWidth;
    const viewLeft = scroll.scrollLeft;
    const viewRight = viewLeft + scroll.clientWidth;
    if (elLeft < viewLeft) scroll.scrollTo({ left: elLeft - 16, behavior: 'smooth' });
    else if (elRight > viewRight) scroll.scrollTo({ left: elRight - scroll.clientWidth + 16, behavior: 'smooth' });
  }, [activeId]);

  /**
   * 面板定位：右对齐触发按钮，clamp 在 viewport 内，保证永不溢出。
   */
  const computePanelPos = useCallback((item) => {
    const btn = triggerRefs.current[item.id];
    if (!btn) return null;
    const rect = btn.getBoundingClientRect();
    const margin = 12;
    const width = Math.min(item.width || 430, window.innerWidth - margin * 2);
    let left = rect.right - width;
    if (left < margin) left = margin;
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - width);
    }
    return { top: rect.bottom + 10, left, width, columns: item.columns || 3 };
  }, []);

  const openPanel = useCallback((item) => {
    clearTimeout(openTimerRef.current);
    clearTimeout(closeTimerRef.current);
    // 用 ref 判重，避免在 setState updater 内执行副作用（StrictMode 下会执行两次）
    if (openIdRef.current === item.id) return;
    const pos = computePanelPos(item);
    if (!pos) return;
    openIdRef.current = item.id;
    setOpenId(item.id);
    setPanelPos(pos);
    setPanelMounted(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setPanelEntered(true)));
  }, [computePanelPos]);

  const closePanel = useCallback(() => {
    clearTimeout(openTimerRef.current);
    clearTimeout(closeTimerRef.current);
    openIdRef.current = null;
    setOpenId(null);
    setPanelEntered(false);
    closeTimerRef.current = setTimeout(() => setPanelMounted(false), 180);
  }, []);

  /** 桌面：hover 已展开 → 点击进入枢纽主页；触屏：首次展开、再次进入 */
  const togglePanel = useCallback((item) => {
    if (openIdRef.current === item.id) {
      closePanel();
      if (item.path) navigate(item.path);
    } else {
      openPanel(item);
    }
  }, [openPanel, closePanel, navigate]);

  /** hover 打开（90ms 防误触），移出后 200ms 关闭 */
  const onPanelEnter = useCallback((item) => () => {
    clearTimeout(closeTimerRef.current);
    clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => openPanel(item), 90);
  }, [openPanel]);

  const onPanelLeave = useCallback(() => {
    clearTimeout(openTimerRef.current);
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(closePanel, 200);
  }, [closePanel]);

  /* 点击外部 / Esc / 滚动 / 缩放时关闭，并同步位置 */
  useEffect(() => {
    if (!openId) return;

    const onMouseDown = (e) => {
      const target = e.target;
      const panel = document.getElementById('simon-nav-panel');
      if (panel && panel.contains(target)) return;
      const hit = Object.values(triggerRefs.current).some(
        (el) => el && el.contains(target)
      );
      if (hit) return;
      closePanel();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') closePanel();
    };
    const onReflow = () => {
      const id = openIdRef.current;
      if (!id) return;
      const item = NAVIGATION.find((i) => i.id === id);
      if (!item) return;
      const pos = computePanelPos(item);
      if (pos) setPanelPos(pos);
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [openId, closePanel, computePanelPos]);

  /** 路由变化时收起面板 */
  useEffect(() => {
    closePanel();
  }, [location.pathname, closePanel]);

  /** 网格卡片点击，带 scale(0.96) 触感回弹 */
  const handleChildClick = useCallback((child) => {
    setPressing(child.path);
    setTimeout(() => setPressing(null), 160);
    navigate(child.path);
    closePanel();
  }, [navigate, closePanel]);

  return (
    <Layout
      className="sc-forest-shell"
      style={{ minHeight: '100vh', position: 'relative', zIndex: 0, background: 'transparent' }}
    >
      <style>{NAV_CSS}</style>

      {/* 萤火虫背景 —— 置于毛玻璃之上，pointerEvents: none，不占空间不拦操作 */}
      <div className="sc-firefly-layer" style={{ position: 'fixed', inset: 0, zIndex: 2, pointerEvents: 'none' }}>
        <FireflyCanvas speed={0.6} density={0.4} mouseInteraction={false} />
      </div>

      <Header
        style={{
          position: 'sticky', top: 0, zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          /* 三段式间距：左 24 / 中 32 (space-between 自动) / 右 24 */
          padding: '0 20px', height: 64, gap: 16,
          /* 毛玻璃 —— 半透明黑，让森林背景从导航边缘透出 */
          background: scrolled ? 'rgba(0, 0, 0, 0.55)' : 'rgba(0, 0, 0, 0.45)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.07)',
          boxShadow: 'none',
          transition: 'background 300ms var(--ease)',
        }}
      >
        {/* Logo —— 左侧 */}
        <div
          onClick={() => navigate('/community')}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            cursor: 'pointer', flexShrink: 0, padding: '4px 8px', borderRadius: 10,
            marginRight: 8,
          }}
        >
          <img src="/logo-cat-white.png" alt="西蒙的猫" style={{ width: 28, height: 28 }} />
          <span style={{
            color: 'var(--text-primary)', fontWeight: 500, fontSize: 15,
            letterSpacing: '0.02em', whiteSpace: 'nowrap', fontFamily: 'var(--font-sans)',
          }}>
            西蒙的猫
          </span>
        </div>

        {/* —— 单行高密度 Tab 栏 —— */}
        <nav
          ref={navScrollRef}
          className="sc-nav-scroll"
          style={{ flex: 1, minWidth: 0 }}
        >
          <div
            ref={navInnerRef}
            style={{
              position: 'relative',
              display: 'flex', alignItems: 'center',
              gap: 0, padding: '0 4px', width: 'max-content', minWidth: '100%',
            }}
          >
            {NAVIGATION.map((item) => {
              const isActive = activeId === item.id;
              const isOpen = openId === item.id;
              const isDropdown = !!item.children;

              const base = {
                display: 'flex', alignItems: 'center', gap: 5,
                height: 32, padding: '0 11px',
                borderRadius: 18,
                fontSize: 12.5, fontWeight: isActive ? 600 : 500,
                lineHeight: 1, whiteSpace: 'nowrap',
                fontFamily: 'var(--font-sans)', cursor: 'pointer',
                transition: 'background 200ms var(--ease), color 200ms var(--ease), border-color 200ms var(--ease)',
              };

              /* AI OS —— 胶囊高亮 */
              const capsuleStyle = item.isCapsule
                ? {
                    background: isActive
                      ? 'rgba(232, 210, 138, 0.2)'
                      : 'rgba(232, 210, 138, 0.12)',
                    border: `1px solid ${isActive
                      ? 'rgba(232, 210, 138, 0.42)'
                      : 'rgba(232, 210, 138, 0.26)'}`,
                    color: isActive ? '#F2E0A6' : 'var(--accent)',
                  }
                : {
                    background: isActive ? 'rgba(255,255,255,0.08)' : 'transparent',
                    border: '1px solid transparent',
                    color: isActive ? 'var(--text-primary)' : 'rgba(240, 245, 250, 0.7)',
                  };

              return (
                <div
                  key={item.id}
                  ref={(el) => {
                    if (isDropdown) triggerRefs.current[item.id] = el;
                  }}
                  data-tab-id={item.id}
                  data-tab-active={isActive ? 'true' : 'false'}
                  style={{ position: 'relative', display: 'flex', flexShrink: 0 }}
                  onMouseEnter={isDropdown ? onPanelEnter(item) : undefined}
                  onMouseLeave={isDropdown ? onPanelLeave : undefined}
                >
                  <button
                    onClick={() => (isDropdown ? togglePanel(item) : navigate(item.path))}
                    aria-haspopup={isDropdown ? 'true' : undefined}
                    aria-expanded={isDropdown ? isOpen : undefined}
                    style={{ ...base, ...capsuleStyle }}
                    onMouseEnter={(e) => {
                      if (isActive) return;
                      if (item.isCapsule) {
                        e.currentTarget.style.background = 'rgba(232, 210, 138, 0.18)';
                        e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.36)';
                      } else {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                        e.currentTarget.style.color = 'var(--text-primary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isActive) return;
                      if (item.isCapsule) {
                        e.currentTarget.style.background = 'rgba(232, 210, 138, 0.12)';
                        e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.26)';
                      } else {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'rgba(240, 245, 250, 0.7)';
                      }
                    }}
                  >
                    {item.icon && (
                      <span style={{
                        fontSize: 12.5, lineHeight: 1,
                        opacity: isActive ? 0.95 : 0.7,
                        display: 'inline-flex', alignItems: 'center',
                      }}>
                        {item.icon}
                      </span>
                    )}
                    {item.label}
                    {isDropdown && (
                      <DownOutlined
                        style={{
                          fontSize: 8, opacity: 0.7, marginLeft: 1,
                          transform: isOpen ? 'rotate(180deg)' : 'none',
                          transition: 'transform 220ms var(--ease)',
                        }}
                      />
                    )}
                  </button>
                </div>
              );
            })}

            {/* 滑动下划线 —— 单个共享元素，transform 平滑过渡 */}
            <span
              style={{
                position: 'absolute',
                bottom: -6,
                height: 2,
                borderRadius: 2,
                background: 'var(--accent)',
                left: 0,
                width: indicator.width,
                opacity: indicator.ready ? 1 : 0,
                transform: `translateX(${indicator.left}px)`,
                transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1), width 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms var(--ease)',
                pointerEvents: 'none',
              }}
            />
          </div>
        </nav>

        {/* 右侧常驻：语言 / 消息 / + 创作 / 登录·头像 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button
            onClick={switchLanguage}
            aria-label="switch language"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, padding: 0, borderRadius: 10,
              border: '1px solid rgba(255, 255, 255, 0.08)',
              background: 'rgba(255, 255, 255, 0.04)',
              color: 'var(--text-secondary)', cursor: 'pointer',
              fontSize: 12, fontWeight: 500, fontFamily: 'var(--font-sans)',
              lineHeight: 1, transition: 'background 150ms ease, color 150ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            {i18n.language === 'en-US' ? '中' : 'En'}
          </button>

          <button
            onClick={() => navigate('/messages')}
            aria-label="messages"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, padding: 0, borderRadius: 10,
              border: '1px solid rgba(255, 255, 255, 0.08)',
              background: 'rgba(255, 255, 255, 0.04)',
              color: location.pathname.startsWith('/messages')
                ? 'var(--accent)' : 'var(--text-secondary)',
              cursor: 'pointer', lineHeight: 1,
              transition: 'background 150ms ease, color 150ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)'; }}
          >
            <MessageOutlined style={{ fontSize: 15, lineHeight: 1 }} />
          </button>

          {/* + 创作 —— 品牌渐变 + 大圆角 + 呼吸流光 */}
          <button
            onClick={() => navigate('/projects')}
            style={{
              position: 'relative', overflow: 'hidden',
              display: 'flex', alignItems: 'center', gap: 5,
              height: 40, padding: '0 16px', borderRadius: 40, border: 'none',
              background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
              color: '#FFFFFF', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, lineHeight: 1,
              fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap',
              animation: 'scBreathe 3.2s ease-in-out infinite',
              transition: 'transform 140ms var(--ease)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
          >
            <PlusOutlined style={{ fontSize: 12, lineHeight: 1 }} />
            <span style={{ lineHeight: 1 }}>创作</span>
            <span
              style={{
                position: 'absolute', top: 0, bottom: 0, width: '38%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent)',
                animation: 'scShimmer 2.8s ease-in-out infinite',
                pointerEvents: 'none',
              }}
            />
          </button>

          {!user ? (
            <button
              onClick={() => navigate('/login')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: 40, padding: '0 16px', borderRadius: 10,
                background: '#E8D28A',
                border: '1px solid rgba(255, 255, 255, 0.16)',
                color: '#050D1A', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-sans)',
                lineHeight: 1, whiteSpace: 'nowrap',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.28)',
                transition: 'background 150ms ease, box-shadow 150ms ease, transform 120ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F2E0A6'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(232, 210, 138, 0.28)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#E8D28A'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.28)'; e.currentTarget.style.transform = 'none'; }}
            >
              登录
            </button>
          ) : (
            <div
              onClick={() => navigate('/settings')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 40, height: 40, borderRadius: 10, cursor: 'pointer',
              }}
            >
              <Avatar size={32} style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                fontSize: 13, fontWeight: 600, color: '#ffffff',
                border: '1px solid rgba(139, 92, 246, 0.3)', lineHeight: 1,
              }}>
                {user?.nickname?.[0] || 'U'}
              </Avatar>
            </div>
          )}
        </div>
      </Header>

      {/* 下拉面板（portal 挂载，避免被 Header overflow 裁剪） */}
      {panelMounted && panelPos && typeof document !== 'undefined' && createPortal(
        <div
          id="simon-nav-panel"
          onMouseEnter={() => {
            clearTimeout(closeTimerRef.current);
            clearTimeout(openTimerRef.current);
          }}
          onMouseLeave={onPanelLeave}
          style={{
            position: 'fixed',
            top: panelPos.top,
            left: panelPos.left,
            width: panelPos.width,
            background: 'rgba(9, 20, 38, 0.94)',
            backdropFilter: 'blur(24px) saturate(160%)',
            WebkitBackdropFilter: 'blur(24px) saturate(160%)',
            border: '1px solid rgba(255, 255, 255, 0.09)',
            borderRadius: 16,
            padding: 18,
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.52)',
            zIndex: 2000,
            fontFamily: 'var(--font-sans)',
            opacity: panelEntered ? 1 : 0,
            transform: panelEntered ? 'translateY(0)' : 'translateY(-6px)',
            transition: 'opacity 180ms var(--ease), transform 180ms var(--ease)',
            pointerEvents: panelEntered ? 'auto' : 'none',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${panelPos.columns}, minmax(0, 1fr))`,
              gap: 8,
            }}
          >
            {(() => {
              const item = NAVIGATION.find((i) => i.id === openId);
              return item?.children?.map((child) => {
                const isActive = activePath === child.path;
                return (
                  <div
                    key={child.path}
                    onClick={() => handleChildClick(child)}
                    style={{
                      position: 'relative',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 9,
                      padding: '15px 8px', borderRadius: 12,
                      cursor: 'pointer',
                      background: isActive ? 'var(--accent-soft)' : 'rgba(255, 255, 255, 0.03)',
                      border: `1px solid ${isActive
                        ? 'rgba(232, 210, 138, 0.28)'
                        : 'rgba(255, 255, 255, 0.06)'}`,
                      color: isActive ? 'var(--accent)' : 'rgba(240, 245, 250, 0.82)',
                      transform: pressing === child.path ? 'scale(0.96)' : 'scale(1)',
                      transition: 'transform 160ms cubic-bezier(0.34, 1.56, 0.64, 1), background 180ms var(--ease), border-color 180ms var(--ease)',
                    }}
                    onMouseEnter={(e) => {
                      if (pressing !== child.path) e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.09)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.16)';
                    }}
                    onMouseLeave={(e) => {
                      if (pressing !== child.path) e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.background = isActive
                        ? 'var(--accent-soft)' : 'rgba(255, 255, 255, 0.03)';
                      e.currentTarget.style.borderColor = isActive
                        ? 'rgba(232, 210, 138, 0.28)' : 'rgba(255, 255, 255, 0.06)';
                    }}
                  >
                    <span style={{ fontSize: 18, lineHeight: 1, opacity: isActive ? 1 : 0.85 }}>
                      {child.icon}
                    </span>
                    <span style={{ fontSize: 12, lineHeight: 1, whiteSpace: 'nowrap' }}>
                      {child.label}
                    </span>
                    {child.showBadge && pendingCount > 0 && (
                      <span style={{
                        position: 'absolute', top: 8, right: 10,
                        fontSize: 10, fontWeight: 600, color: '#fff',
                        background: '#D98282', borderRadius: 8,
                        padding: '0 5px', minWidth: 16, textAlign: 'center',
                        lineHeight: '16px',
                      }}>{pendingCount}</span>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>,
        document.body
      )}

      <Layout style={{ background: 'transparent', position: 'relative', zIndex: 1 }}>
        <Content className="sc-content-glass" style={{ minHeight: 'calc(100vh - 64px)', background: 'transparent' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
